import classNames from 'classnames'
import { Box, Button, Card, CardContent, CardMedia, Chip, ExternalLinkIcon, styled, Typography } from 'opensolar-ui'

export type TemplateCardProps = {
  backgroundColor?: string
  body?: string
  buttonText?: string
  disabled: boolean
  header?: string
  height?: string | number
  id?: string
  imgSrc?: string
  /** When set with `onButtonClick`, opens the same CTA as create-modal link cards. */
  onButtonClick?: () => void
  onSelect?: () => Promise<void>
  selected: boolean
  tag?: string
  testId?: string
}

export type CustomTemplateCardProps = Omit<TemplateCardProps, 'tag' | 'header' | 'body' | 'imgSrc'>

export const BaseTemplateCard = styled(Card)({
  position: 'relative',
  width: '264px',
  borderRadius: '12px',
  border: '1px solid #DCDCDE',
  '&:hover': {
    border: '1px solid #94B0EC',
    boxShadow: '0 0 0 1px #94B0EC',
  },
  '.MuiCardMedia-root': {
    height: '148px',
  },
  '.MuiCardContent-root': {
    padding: '12px',
  },
})

export const TruncatedTypography = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  '-webkit-box-orient': 'vertical',
  '-webkit-line-clamp': '3',
})

export const SingleLineTypography = styled(TruncatedTypography)({
  '-webkit-line-clamp': '1',
  whiteSpace: 'nowrap',
})

const FooterActionRow = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
})

const BodyWithFooter = styled(TruncatedTypography)({
  '-webkit-line-clamp': '2',
  flex: 1,
})

const StyledCard = styled(BaseTemplateCard)(({ height }: { height?: TemplateCardProps['height'] }) => ({
  cursor: 'pointer',
  minHeight: '254px',
  ...(height != null && {
    height: typeof height === 'number' ? `${height}px` : height,
  }),
  '&.selected': {
    border: '1px solid #4272DD',
    boxShadow: '0 0 0 1px #4272DD',
  },
  '&.disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  '&.template-card--footer': {
    cursor: 'default',
    display: 'flex',
    flexDirection: 'column',
  },
  '&.template-card--footer:not(.disabled)': {
    cursor: 'pointer',
  },
  '&.template-card--footer.template-card--select-disabled': {
    cursor: 'default',
  },
  '&.template-card--footer.template-card--select-disabled.disabled': {
    cursor: 'not-allowed',
  },
  '&.template-card--footer .MuiCardContent-root': {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  '.MuiCardContent-root': {
    '.MuiTypography-body1:first-of-type': {
      fontWeight: '600',
      lineHeight: '20px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '.MuiTypography-body1:last-of-type': {
      fontWeight: '400',
      lineHeight: '20px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      '-webkit-line-clamp': '3',
      '-webkit-box-orient': 'vertical',
    },
  },
}))

const Tag = styled(Chip)({
  position: 'absolute',
  top: '8px',
  right: '8px',
  borderRadius: '6px',
  backgroundColor: '#FFFFFFF2',
  height: '28px',
  '& .MuiChip-label, .OSUI-Chip-label': {
    padding: '8px',
  },
})

const TemplateCard: React.FC<TemplateCardProps> = ({
  backgroundColor,
  body,
  buttonText,
  disabled,
  header,
  height,
  id,
  imgSrc,
  onButtonClick,
  onSelect,
  selected,
  tag,
  testId,
}) => {
  const hasFooterButton = Boolean(buttonText && onButtonClick)
  const selectable = Boolean(onSelect)

  return (
    <StyledCard
      elevation={0}
      className={classNames({
        disabled,
        selected,
        'template-card--footer': hasFooterButton,
        'template-card--select-disabled': hasFooterButton && !selectable,
      })}
      height={height ?? '100%'}
      id={id}
      onClick={
        selectable && !disabled
          ? async () => {
              await onSelect?.()
            }
          : undefined
      }
      {...(backgroundColor && { style: { backgroundColor } })}
      {...(testId && { 'data-testid': testId })}
      
    >
      {tag && <Tag label={tag} />}
      <CardMedia image={imgSrc} />
      <CardContent>
        <SingleLineTypography colorHex="#4C4B53" variant="body2SemiBold">
          {header}
        </SingleLineTypography>
        {hasFooterButton ? (
          <>
            <BodyWithFooter colorHex="#6B6A76" variant="body2">
              {body}
            </BodyWithFooter>
            <FooterActionRow>
              <Button
                disabled={disabled}
                endIcon={<ExternalLinkIcon size={14} />}
                size="small"
                testId={testId}
                variant="contained"
                onClick={(event) => {
                  event.stopPropagation()
                  if (!disabled) {
                    onButtonClick?.()
                  }
                }}
              >
                {buttonText}
              </Button>
            </FooterActionRow>
          </>
        ) : (
          <TruncatedTypography colorHex="#6B6A76" variant="body2">
            {body}
          </TruncatedTypography>
        )}
      </CardContent>
    </StyledCard>
  )
}

export default TemplateCard
